import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, MapPin, Phone, Globe, Clock, Navigation, Bookmark, X, Pencil, Flag } from 'lucide-react';
import type { Place, Review } from '@wingman/shared';
import { api } from '../../lib/api.js';
import { useAuthStore } from '../../store/authStore.js';
import { useIsMobile } from '../../hooks/useMediaQuery.js';
import ReviewModal from './ReviewModal.js';

interface PlaceDetailsProps {
  place: Place;
  onClose: () => void;
}

export default function PlaceDetails({ place, onClose }: PlaceDetailsProps) {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [reportMessage, setReportMessage] = useState<string | null>(null);
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  useEffect(() => {
    setLoading(true);
    api.places
      .getReviews(place.id)
      .then((res) => setReviews(res.data))
      .finally(() => setLoading(false));
  }, [place.id]);

  const handleSave = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (saved) {
      await api.user.unsavePlace(place.id);
      setSaved(false);
    } else {
      await api.user.savePlace(place.id);
      setSaved(true);
    }
  };

  const handleDirections = () => {
    navigate(`/directions?toLat=${place.lat}&toLng=${place.lng}&toLabel=${encodeURIComponent(place.name)}`);
  };

  const handleReportClosed = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    setReporting(true);
    setReportMessage(null);
    try {
      await api.places.reportIssue(place.id, { kind: 'closed', note: 'Reported closed by a map user' });
      setReportMessage('Thanks. Your closure report was submitted for verification.');
    } catch (error) {
      setReportMessage(error instanceof Error ? error.message : 'Could not submit report.');
    } finally {
      setReporting(false);
    }
  };

  const handleReviewSubmitted = () => {
    setShowReviewModal(false);
    setLoading(true);
    api.places
      .getReviews(place.id)
      .then((res) => setReviews(res.data))
      .finally(() => setLoading(false));
  };

  if (isMobile) {
    return (
      <>
        <div className="bottom-sheet bottom-sheet-expanded z-30 glass-panel">
          <div className="bottom-sheet-handle" />
          <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 p-4 flex items-center justify-between">
            <h2 className="font-bold text-lg text-gray-900 dark:text-gray-100 truncate">{place.name}</h2>
            <button onClick={onClose} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 p-1">
              <X size={20} />
            </button>
          </div>
          <div className="p-4 space-y-4">
            <PlaceDetailsContent
              place={place}
              reviews={reviews}
              loading={loading}
              saved={saved}
              user={user}
              onDirections={handleDirections}
              onSave={handleSave}
              onWriteReview={() => user ? setShowReviewModal(true) : navigate('/login')}
              onReportClosed={handleReportClosed}
              reporting={reporting}
              reportMessage={reportMessage}
            />
          </div>
        </div>
        {showReviewModal && (
          <ReviewModal
            placeId={place.id}
            placeName={place.name}
            onClose={() => setShowReviewModal(false)}
            onSubmitted={handleReviewSubmitted}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div className="absolute top-0 right-0 h-full w-96 max-w-[calc(100vw-0px)] sidebar z-20 glass-panel">
        <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 p-4 flex items-center justify-between">
          <h2 className="font-bold text-lg text-gray-900 dark:text-gray-100 truncate">{place.name}</h2>
          <button onClick={onClose} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 p-1">
            <X size={20} />
          </button>
        </div>
        <div className="p-4 space-y-4">
          <PlaceDetailsContent
            place={place}
            reviews={reviews}
            loading={loading}
            saved={saved}
            user={user}
            onDirections={handleDirections}
            onSave={handleSave}
            onWriteReview={() => user ? setShowReviewModal(true) : navigate('/login')}
            onReportClosed={handleReportClosed}
            reporting={reporting}
            reportMessage={reportMessage}
          />
        </div>
      </div>
      {showReviewModal && (
        <ReviewModal
          placeId={place.id}
          placeName={place.name}
          onClose={() => setShowReviewModal(false)}
          onSubmitted={handleReviewSubmitted}
        />
      )}
    </>
  );
}

interface PlaceDetailsContentProps {
  place: Place;
  reviews: Review[];
  loading: boolean;
  saved: boolean;
  user: { name: string } | null;
  onDirections: () => void;
  onSave: () => void;
  onWriteReview: () => void;
  onReportClosed: () => void;
  reporting: boolean;
  reportMessage: string | null;
}

function PlaceDetailsContent({ place, reviews, loading, saved, user, onDirections, onSave, onWriteReview, onReportClosed, reporting, reportMessage }: PlaceDetailsContentProps) {
  return (
    <>
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          {place.categoryName && (
            <span className="inline-flex items-center rounded-full bg-brand-50 px-3 py-1 text-xs font-semibold capitalize text-brand-700 dark:bg-brand-900/40 dark:text-brand-300">
              {place.categoryName}
            </span>
          )}
          <h2 className="mt-2 text-2xl font-semibold tracking-tight text-gray-950 dark:text-white">{place.name}</h2>
        </div>
        <span className="rounded-full bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">Explore</span>
      </div>

      {place.freshnessStatus && place.freshnessStatus !== 'verified' && (
        <div className="rounded border border-amber-400/30 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
          {place.freshnessStatus === 'reported' ? 'This place has been reported for verification.' : `Listing freshness: ${place.freshnessStatus}.`}
          {place.lastCheckedAt && ` Last checked ${new Date(place.lastCheckedAt).toLocaleDateString()}.`}
        </div>
      )}

      {place.rating !== undefined && place.rating > 0 && (
        <div className="flex items-center gap-2">
          <div className="flex">
            {[1, 2, 3, 4, 5].map((n) => (
              <Star
                key={n}
                size={18}
                className={
                  n <= Math.round(place.rating!)
                    ? 'fill-amber-400 text-amber-400'
                    : 'text-gray-300 dark:text-gray-600'
                }
              />
            ))}
          </div>
          <span className="font-semibold text-gray-800 dark:text-gray-200">{place.rating.toFixed(1)}</span>
          <span className="text-sm text-gray-400 dark:text-gray-500">({place.reviewCount} reviews)</span>
        </div>
      )}

      {place.address && (
        <div className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
          <MapPin size={16} className="flex-shrink-0 mt-0.5" />
          <span>{place.address}</span>
        </div>
      )}

      {place.phone && (
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          <Phone size={16} className="flex-shrink-0" />
          <a href={`tel:${place.phone}`} className="hover:text-brand-600">
            {place.phone}
          </a>
        </div>
      )}

      {place.website && (
        <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
          <Globe size={16} className="flex-shrink-0" />
          <a
            href={place.website}
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-brand-600 truncate"
          >
            {place.website}
          </a>
        </div>
      )}

      {place.hours && Object.keys(place.hours).length > 0 && (
        <div className="flex items-start gap-2 text-sm text-gray-600 dark:text-gray-400">
          <Clock size={16} className="flex-shrink-0 mt-0.5" />
          <div>
            {Object.entries(place.hours).map(([day, hours]) => (
              <div key={day} className="flex justify-between gap-4">
                <span className="capitalize">{day}</span>
                <span>{hours}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {place.photos && place.photos.length > 0 && (
        <div className="grid grid-cols-2 gap-2">
          {place.photos.map((photo) => (
            <img
              key={photo.id}
              src={(photo as { thumbnailUrl?: string }).thumbnailUrl || photo.url}
              alt={place.name}
              loading="lazy"
              decoding="async"
              className="w-full h-32 object-cover rounded-lg"
            />
          ))}
        </div>
      )}

      <div className="flex gap-2 border-t border-gray-200/70 pt-4 dark:border-gray-700/70">
        <button onClick={onDirections} className="btn btn-primary flex-1 flex items-center justify-center gap-2 shadow-lg shadow-brand-600/20">
          <Navigation size={16} />
          Directions
        </button>
        <button
          onClick={onSave}
          className={`btn flex items-center justify-center gap-2 ${saved ? 'btn-primary' : 'btn-secondary'}`}
        >
          <Bookmark size={16} />
          {saved ? 'Saved' : 'Save'}
        </button>
      </div>

      <button
        onClick={onWriteReview}
        className="btn btn-secondary w-full flex items-center justify-center gap-2"
      >
        <Pencil size={16} />
        Write a Review
      </button>

      <button
        type="button"
        onClick={onReportClosed}
        disabled={reporting}
        className="w-full text-xs text-gray-500 hover:text-amber-600 dark:text-gray-400 dark:hover:text-amber-300"
      >
        <Flag size={13} className="mr-1 inline" />
        {reporting ? 'Submitting report…' : 'Report this place as closed'}
      </button>
      {reportMessage && <p className="text-xs text-gray-500 dark:text-gray-400">{reportMessage}</p>}

      <div className="pt-4 border-t border-gray-100 dark:border-gray-800">
        <h3 className="font-semibold text-gray-900 dark:text-gray-100 mb-3">Reviews</h3>
        {loading ? (
          <p className="text-sm text-gray-400 dark:text-gray-500">Loading reviews...</p>
        ) : reviews.length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500">No reviews yet. Be the first to review!</p>
        ) : (
          <div className="space-y-3">
            {reviews.map((review) => (
              <div key={review.id} className="border-b border-gray-50 dark:border-gray-800 pb-3 last:border-0">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-8 h-8 rounded-full bg-brand-100 dark:bg-brand-900 flex items-center justify-center text-brand-700 dark:text-brand-300 text-sm font-medium">
                    {review.userName.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-800 dark:text-gray-200">{review.userName}</div>
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star
                          key={n}
                          size={12}
                          className={
                            n <= review.rating
                              ? 'fill-amber-400 text-amber-400'
                              : 'text-gray-300 dark:text-gray-600'
                          }
                        />
                      ))}
                    </div>
                  </div>
                </div>
                {review.text && <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{review.text}</p>}
                {review.photos && review.photos.length > 0 && (
                  <div className="flex gap-2 mt-2">
                    {review.photos.map((photo) => (
                      <img
                        key={photo.id}
                        src={(photo as { thumbnailUrl?: string }).thumbnailUrl || photo.url}
                        alt="Review"
                        loading="lazy"
                        decoding="async"
                        className="w-16 h-16 object-cover rounded"
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
